import Creative from "../Creative";
import {handlersNames} from "../handlers";
import {extraKeys, genderKeys, contextTypeKeys, bodyKeys} from "../etc";
import Processing from "../Processing";
import { generateSeeds } from "./helper";

export function createAnimatedCreative(processing, original, sourceCreative, autosave) {
  const step = {
    id: -1,
    images: [
      {src: original, useAltBody: true},
      {src: sourceCreative.result, useAltBody: true}
    ],
    templateParams: {},
    setAsFile: "raw",
  };

  if (sourceCreative.getExtra(extraKeys.collageType) === "video") {
    step.id = 8792;
    step.templateParams.animation = true;
  } else {
    if (processing.getExtra(Processing.EXTRA_IS_MULTIFACE)) {
      step.id = 8812;
    } else {
      step.id = 8811;
    }
  }

  const steps = [step];
  const id = steps.map((step) => step.id).join("_");

  const creative = new Creative();
  creative.setGroup("video");
  creative.setTemplateId(id);
  creative.setHandler(handlersNames.COMBO);
  creative.setExtra(Creative.EXTRA_COMBO_STEPS, steps);
  creative.setExtra("source_id", sourceCreative.id);

  if (sourceCreative.hasExtra(extraKeys.seeds)) {
    creative.setExtra(extraKeys.seeds, sourceCreative.getExtra(extraKeys.seeds));
  }

  if (autosave) {
    creative.setExtra(Creative.EXTRA_AUTOSAVE, true);
  }

  return creative;
}

export function createCreativeByConfig(processing, config, context) {
  if (config.type === "barbify") {
    return createCreativeByBarbifyConfig(processing, config, context);
  } else if (config.type === "hotify_d14") {
    return createCreativeByHotifyD14Config(processing, config, context);
  } else if (config.type === "bodyeditor_20230630_d12") {
    return createCreativeByBodyEditor20230630D12Config(processing, config, context);
  } else if (config.type === "d22sdxl") {
    return createCreativeByD22sdxlConfig(processing, config, context);
  } else if (config.type === "ipadapter") {
    return createCreativeByIpadapterConfig(processing, config, context);
  } else {
    return createCreativeByHotifyConfig(processing, config, context);
  }
}

function createCreativeByHotifyConfig(processing, config, context) {
  const templateGender = transformGender(context.gender);
  const templateId = (context.contextType === contextTypeKeys.template) ? 8778 : 8779;
  const stylizations = {
    no: [
      {id: 6472},
    ],
    glamour: [
      context.contextType === contextTypeKeys.template
        ? {id: 7088}
        : {id: 8799},
    ],
  };

  const contextTypesConfig = {
    [contextTypeKeys.template]: {
      canvas_template_name: 8663,
      mask_template_name: 8664,
      pose_template_name: 8663,
      param_ctmdl: "dm_of", // ctrlnet_modules: depth_midas, openpose_full
    },
    [contextTypeKeys.original]: {
      canvas_template_name: 8659,
      mask_template_name: 8790,
      pose_template_name: 8663,
      param_ctmdl: "dm_of", // ctrlnet_modules: depth_midas, openpose_full
      mask_blur: 4,
      param_ipmki: 0, // inpainting_mask_invert
      param_ctwg: "0.7, 1", // control_weights
    },
  };

  const templateStep = {
    id: templateId,
    templateParams: {
      "@transformationId": "hotify",
      param_pp: "true, true", // pixel_perfect
      param_ctwg: "0.5, 1", // control_weights
      param_ctmds: "2, 1", // control_modes
      param_ns: 30, // num_steps
      param_s: 0.8, // strength
      ...contextTypesConfig[context.contextType],
      ...config.params.defaults,
      gender: templateGender,
    },
    setAsFile: "raw",
    // ignorePhotolabError: {code: 1},
    timeout: 150_000,
  };

  const variant = context.contextType === contextTypeKeys.original
    ? "original"
    : (context.body === bodyKeys.slim ? "slim" : "curve");

  templateStep.templateParams = {
    ...templateStep.templateParams,
    ...config.params[variant].params,
    "@replacements": [
      {
        parameters: ["prompt"],
        search: "{gender}",
        mode: "ig",
        replacement: templateGender,
      }
    ],
  }

  const steps = [
    ...stylizations[context.stylization],
    templateStep,
  ];

  steps.first().images = [{src: "@", useAltBody: true}];

  const creative = new Creative();
  creative.setGroup("general");
  creative.setTemplateId(config.name);
  creative.setHandler(handlersNames.COMBO);
  creative.setExtra(Creative.EXTRA_COMBO_STEPS, steps);
  creative.setExtra(extraKeys.gender, context.gender);
  creative.setExtra(extraKeys.stylization, context.stylization);
  creative.setExtra(extraKeys.body, context.body);
  creative.setExtra(extraKeys.contextType, context.contextType);
  creative.setExtra(extraKeys.collageType, window.clientConfig.features.collageWithAnimation ? "video" : "image");

  return creative;
}

// только original
function createCreativeByHotifyD14Config(processing, config, context) {
  const templateGender = transformGender(context.gender);
  // const variant = context.contextType === contextTypeKeys.original
  //   ? "original"
  //   : (context.body === bodyKeys.slim ? "slim" : "curve");

  const templateStep = {
    id: (context.contextType === contextTypeKeys.template) ? 8778 : 8779,
    templateParams: {
      "@transformationId": "hotify",
      "@replacements": [
        {
          parameters: ["prompt"],
          search: "{gender}",
          mode: "ig",
          replacement: templateGender,
        }
      ],
      ...config.params.defaults,
      ...config.params.original.params,
      gender: templateGender,
    },
    setAsFile: "raw",
    // ignorePhotolabError: {code: 1},
    timeout: 150_000,
  };

  const steps = [
    templateStep,
  ];

  steps.first().images = [{src: "@", useAltBody: true}];

  const creative = new Creative();
  creative.setGroup("general");
  creative.setTemplateId(config.name);
  creative.setHandler(handlersNames.COMBO);
  creative.setExtra(Creative.EXTRA_COMBO_STEPS, steps);
  creative.setExtra(extraKeys.gender, context.gender);
  creative.setExtra(extraKeys.stylization, context.stylization);
  creative.setExtra(extraKeys.body, context.body);
  creative.setExtra(extraKeys.contextType, context.contextType);
  creative.setExtra(extraKeys.collageType, window.clientConfig.features.collageWithAnimation ? "video" : "image");

  return creative;
}

function createCreativeByBodyEditor20230630D12Config(processing, config, context) {
  const templateGender = transformGender(context.gender);
  const steps = [
    {
      id: 7088,
      images: [{src: "@", useAltBody: true}],
    },
    {
      images: [{src: "#0"}],
      templateParams: {
        "@transformationId": "hotify",
        "@replacements": [
          {
            parameters: ["prompt"],
            search: "{gender}",
            mode: "ig",
            replacement: templateGender,
          }
        ],
        ...config.params.default.params,
        gender: templateGender,
      },
      setAsFile: "raw",
      // ignorePhotolabError: {code: 1},
      timeout: 150_000,
    },
  ];

  const creative = new Creative();
  creative.setGroup("general");
  creative.setTemplateId(config.name);
  creative.setHandler(handlersNames.COMBO);
  creative.setExtra(Creative.EXTRA_COMBO_STEPS, steps);
  creative.setExtra(extraKeys.gender, context.gender);
  creative.setExtra(extraKeys.stylization, context.stylization);
  creative.setExtra(extraKeys.body, context.body);
  creative.setExtra(extraKeys.contextType, context.contextType);
  creative.setExtra(extraKeys.collageType, window.clientConfig.features.collageWithAnimation ? "video" : "image");

  return creative;
}

function createCreativeByBarbifyConfig(processing, config, context) {
  const templateGender = transformGender(context.gender);
  const stylizations = {
    no: [
      {id: 6472},
    ],
    glamour: [
      {id: 7088},
    ],
  };

  const templateStep = {
    id: 8681,
    templateParams: {
      ...config.params.default.params,
      "@transformationId": "barbify2",
      canvas_template_name: 8663,
      mask_template_name: 8664,
      gender: templateGender,
      param_ipfl: 1,
      param_gm: "TRUE",
      param_s: 0.4,
    },
    setAsFile: "raw",
  };

  if (context.gender === genderKeys.male) {
    templateStep.templateParams["@replacements"] = [{
      parameters: ["prompt"],
      search: "barbie",
      mode: "ig",
      replacement: "Ken",
    }];
  }

  const steps = [
    ...stylizations[context.stylization],
    templateStep,
  ];

  steps.first().images = [{src: "@", useAltBody: true}];

  const creative = new Creative();
  creative.setGroup("general");
  creative.setTemplateId(config.name);
  creative.setHandler(handlersNames.COMBO);
  creative.setExtra(Creative.EXTRA_COMBO_STEPS, steps);
  creative.setExtra(extraKeys.gender, context.gender);
  creative.setExtra(extraKeys.stylization, context.stylization);
  creative.setExtra(extraKeys.body, context.body);
  creative.setExtra(extraKeys.contextType, context.contextType);
  creative.setExtra(extraKeys.collageType, window.clientConfig.features.collageWithAnimation ? "video" : "image");

  return creative;
}

function createCreativeByD22sdxlConfig(processing, config, context) {
  const creative = new Creative();
  creative.setGroup("general");
  creative.setTemplateId(config.name);
  creative.setHandler(handlersNames.D22SDXL);
  creative.setExtra(extraKeys.gender, context.gender);
  creative.setExtra(extraKeys.stylization, context.stylization);
  creative.setExtra(extraKeys.body, context.body);
  creative.setExtra(extraKeys.contextType, context.contextType);
  creative.setExtra(extraKeys.collageType, window.clientConfig.features.collageWithAnimation ? "video" : "image");
  creative.setExtra("d22sdxl_config_id", config.params.default.params["@configId"]);

  if (config.params.refiner) {
    creative.setExtra("d22sdxl_refiner_id", config.params.refiner.params["@configId"]);
  }

  return creative;
}

function createCreativeByIpadapterConfig(processing, config, context) {
  let seeds = (context.seeds || generateSeeds().shuffle());

  if (processing.getExtra("generateSeeds") && processing.creatives.length) {
    seeds = processing.creatives.slice(-1).first().getExtra(extraKeys.seeds).slice(1);
  }

  const creative = new Creative();
  creative.setGroup("general");
  creative.setTemplateId(config.params[context.body].params["@configId"]);
  creative.setHandler(handlersNames.IPADAPTER);
  creative.setExtra(extraKeys.gender, context.gender);
  creative.setExtra(extraKeys.stylization, context.stylization);
  creative.setExtra(extraKeys.body, context.body);
  creative.setExtra(extraKeys.contextType, context.contextType);
  creative.setExtra(extraKeys.seeds, seeds);
  creative.setExtra(extraKeys.config, config);
  creative.setExtra("ipadapter_config_id", config.params[context.body].params["@configId"]);

  return creative;
}

export function sortConfigsByWeights(configs) {
  const ordered = configs.slice();
  const results = [];

  while (ordered.length > 0) {
    const weightsSum = ordered.reduce((t, config) => {
      return t + (config.extra["weight"] ** 2);
    }, 0);

    let r = Math.floor(Math.random() * (weightsSum + 1));
    const index = ordered.findIndex((config) => {
      r -= config.extra["weight"] ** 2;
      return r <= 0;
    });

    if (index > -1) {
      results.push(...ordered.splice(index, 1));
    }
  }

  return results;
}

function transformGender(gender) {
  return gender.replace(/female/g, "woman").replace(/male/g, "man");
}